import {
  FirestoreCollections,
  Goal,
  ID,
  NewGoal,
  RoleOption,
  SortingMode,
} from '@griply/types';
import { endOfDay } from 'date-fns';

import { detachWrite } from '../detach-write';
import { FirestoreInstance } from '../types/firestore';

export type CreateGoalOptions<
  TDocRef = unknown,
  TCollectionRef = unknown,
  TFieldValue = unknown,
  TTimestamp = unknown,
  TDb = FirestoreInstance,
> = {
  goal: NewGoal;
  userId: ID;
  collection: (
    db: NoInfer<TDb>,
    path: string,
    ...rest: string[]
  ) => TCollectionRef;
  doc: (
    collectionRef: NoInfer<TCollectionRef>,
    ...rest: string[]
  ) => TDocRef & { id: string };
  setDoc: (
    ref: NoInfer<TDocRef>,
    data: Record<string, unknown>,
  ) => Promise<void> | void;
  db: TDb;
  serverTimestamp: () => TFieldValue;
  timestampFromDate: (date: Date) => TTimestamp;
  onWriteError: (error: unknown) => void;
};

export const createGoal = async <
  TDocRef,
  TCollectionRef,
  TFieldValue,
  TTimestamp,
  TDb = FirestoreInstance,
>({
  goal,
  userId,
  collection,
  doc,
  setDoc,
  db,
  serverTimestamp,
  timestampFromDate,
  onWriteError,
}: CreateGoalOptions<
  TDocRef,
  TCollectionRef,
  TFieldValue,
  TTimestamp,
  TDb
>): Promise<Goal> => {
  const collectionRef = collection(db, FirestoreCollections.Goals);
  const newDoc = doc(collectionRef);

  const deadline = goal.deadline ? endOfDay(goal.deadline) : null;

  const newDocData = {
    ...goal,
    id: newDoc.id,
    iconName: goal.iconName ?? null,
    image: goal.image ?? null,
    lifeAreaId: goal.lifeAreaId ?? null,
    deadline: deadline ? timestampFromDate(deadline) : null,
    parentIds: goal.parentIds?.length
      ? Array.from(new Set(goal.parentIds))
      : null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    frozenAt: null,
    completedAt: null,
    archivedAt: null,
    roles: {
      [RoleOption.Owner]: userId,
      [RoleOption.All]: [userId],
    },
  };

  detachWrite(setDoc(newDoc, newDocData), onWriteError);

  const now = new Date();

  return {
    ...goal,
    id: newDoc.id,
    iconName: goal.iconName ?? null,
    image: null,
    lifeAreaId: goal.lifeAreaId ?? null,
    parentIds: goal.parentIds?.length
      ? Array.from(new Set(goal.parentIds))
      : null,
    startDate: goal.startDate ?? null,
    deadline,
    metric: goal.metric ?? null,
    sections: null,
    taskCount: goal.taskCount ?? null,
    completedTaskCount: goal.completedTaskCount ?? null,
    sortingMode: SortingMode.Custom,
    taskSorting: goal.taskSorting ?? null,
    goalSorting: null,
    createdAt: now,
    updatedAt: now,
    frozenAt: null,
    completedAt: null,
    archivedAt: null,
    roles: {
      [RoleOption.Owner]: userId,
      [RoleOption.All]: [userId],
    },
  };
};
